if (!customElements.get('products-on-you-video-slider')) {
  customElements.define(
    'products-on-you-video-slider',
    class ProductsOnYouVideoGrid extends HTMLElement {
      connectedCallback() {
        this.videos = Array.from(this.querySelectorAll('video'));
        this.controls = Array.from(this.querySelectorAll('[data-video-toggle]'));
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.raf = null;
        this.currentActiveVideo = null;
        this.manuallyPausedVideo = null;

        this.onControlClickHandler = this.onControlClick.bind(this);
        this.onVideoPlayHandler = this.onVideoPlay.bind(this);
        this.updateControlsHandler = this.updateControls.bind(this);
        this.onScroll = this.requestUpdate.bind(this);
        this.onResize = this.requestUpdate.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onReducedMotionChange = this.requestUpdate.bind(this);

        this.controls.forEach((control) => control.addEventListener('click', this.onControlClickHandler));
        this.videos.forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          video.addEventListener('play', this.onVideoPlayHandler);
          video.addEventListener('pause', this.updateControlsHandler);
        });

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (this.reducedMotion.addEventListener) {
          this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
        }

        this.requestUpdate();
      }

      disconnectedCallback() {
        if (this.raf) window.cancelAnimationFrame(this.raf);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);

        if (this.reducedMotion?.removeEventListener) {
          this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
        }

        this.videos?.forEach((video) => {
          video.removeEventListener('play', this.onVideoPlayHandler);
          video.removeEventListener('pause', this.updateControlsHandler);
          video.pause();
        });
        this.controls?.forEach((control) => control.removeEventListener('click', this.onControlClickHandler));
      }

      requestUpdate() {
        if (this.raf) return;

        this.raf = window.requestAnimationFrame(() => {
          this.raf = null;
          this.updateActiveVideo();
        });
      }

      updateActiveVideo() {
        if (!this.autoplayEnabled || this.reducedMotion.matches || document.hidden || !this.isSectionVisible()) {
          this.currentActiveVideo = null;
          this.pauseAll();
          return;
        }

        const activeVideo = this.getActiveVideo();

        if (!activeVideo) {
          this.currentActiveVideo = null;
          this.pauseAll();
          return;
        }

        if (this.manuallyPausedVideo && activeVideo === this.manuallyPausedVideo) {
          this.currentActiveVideo = null;
          this.pauseAll();
          return;
        }

        if (activeVideo !== this.manuallyPausedVideo) {
          this.manuallyPausedVideo = null;
        }

        const activeVideoChanged = activeVideo !== this.currentActiveVideo;
        this.currentActiveVideo = activeVideo;

        this.videos.forEach((video) => {
          if (video === activeVideo) {
            if (!video.paused && !activeVideoChanged) return;

            const playPromise = video.play();
            if (playPromise !== undefined) playPromise.catch(() => {});
          } else {
            video.pause();
          }
        });

        this.updateControls();
      }

      getActiveVideo() {
        const sectionRect = this.getBoundingClientRect();
        const sectionCenter = sectionRect.left + sectionRect.width / 2;
        const visibleVideos = this.videos.filter((video) => {
          const rect = video.getBoundingClientRect();
          const visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
          const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

          return visibleWidth > rect.width * 0.45 && visibleHeight > rect.height * 0.45;
        });

        return visibleVideos.reduce((closestVideo, video) => {
          const rect = video.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - sectionCenter);

          if (!closestVideo || distance < closestVideo.distance) {
            return { video, distance };
          }

          return closestVideo;
        }, null)?.video;
      }

      isSectionVisible() {
        const rect = this.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      }

      pauseAll() {
        this.videos.forEach((video) => video.pause());
        this.updateControls();
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.pauseAll();
          return;
        }

        this.requestUpdate();
      }

      onVideoPlay(event) {
        this.manuallyPausedVideo = null;
        this.currentActiveVideo = event.currentTarget;
        this.videos.forEach((video) => {
          if (video !== event.currentTarget) video.pause();
        });
        this.updateControls();
      }

      onControlClick(event) {
        const button = event.currentTarget;
        const video = button.closest('.products-on-you__card')?.querySelector('video');

        if (!video) return;

        if (video.paused) {
          this.manuallyPausedVideo = null;
          this.videos.forEach((otherVideo) => {
            if (otherVideo !== video) otherVideo.pause();
          });
          const playPromise = video.play();
          if (playPromise !== undefined) playPromise.catch(() => {});
        } else {
          this.manuallyPausedVideo = video;
          video.pause();
        }

        this.updateControls();
      }

      updateControls() {
        this.controls.forEach((button) => {
          const video = button.closest('.products-on-you__card')?.querySelector('video');
          const isPlaying = video && !video.paused;

          button.classList.toggle('is-playing', isPlaying);
          button.setAttribute('aria-label', isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel);
        });
      }
    }
  );
}
